'use client';

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, X, Check, Search, Minus } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import Fuse from 'fuse.js';
export interface MultiSelectOption {
  id: string;
  label: string;
  groupId?: string;
  isGroup?: boolean;
}
export interface MultiSelectGroup {
  id: string;
  label: string;
}
interface MultiSelectDropdownProps {
  label: string | null;
  placeholder: string;
  options: readonly MultiSelectOption[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  showChips?: boolean;
  showSummaryInTrigger?: boolean;
  maxSummaryLength?: number;
  showBulkActions?: boolean;
  disabled?: boolean;
  groups?: MultiSelectGroup[];
  enableVirtualization?: boolean;
  estimateSize?: number;
  showSearch?: boolean;
  searchPlaceholder?: string;
  initialSearchValue?: string;
  selectableGroups?: boolean;
  groupSelectionCascades?: boolean;
}
export function MultiSelectDropdown({
  label,
  placeholder,
  options,
  selectedIds,
  onSelectionChange,
  showChips = true,
  showSummaryInTrigger = false,
  maxSummaryLength = 40,
  showBulkActions = true,
  disabled = false,
  groups = [],
  enableVirtualization = false,
  estimateSize = 40,
  showSearch = false,
  searchPlaceholder = 'Search...',
  initialSearchValue = '',
  selectableGroups = false,
  groupSelectionCascades = true
}: MultiSelectDropdownProps) {
  const [searchQuery, setSearchQuery] = useState(initialSearchValue);
  const [isOpen, setIsOpen] = useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const stripVowels = (s: string) => s.replace(/[aeiou]/gi, '');

  // Precompute search indexes alongside options
  const regularOptions = useMemo(() => options.filter(opt => !opt.isGroup).map(opt_0 => ({
    ...opt_0,
    _spaceless: opt_0.label.replace(/\s+/g, ''),
    _stripped: stripVowels(opt_0.label),
    _strippedSpaceless: stripVowels(opt_0.label.replace(/\s+/g, ''))
  })), [options]);
  const fuse = useRef(new Fuse([], {
    keys: ['label', '_spaceless'],
    threshold: 0.3,
    ignoreLocation: true,
    minMatchCharLength: 1,
    includeScore: true,
    distance: 100
  }));
  const fuseVowelStripped = useRef(new Fuse([], {
    keys: ['_stripped', '_strippedSpaceless'],
    threshold: 0.2,
    ignoreLocation: true,
    minMatchCharLength: 1,
    includeScore: true,
    distance: 100
  }));
  useEffect(() => {
    fuse.current.setCollection(regularOptions);
    fuseVowelStripped.current.setCollection(regularOptions);
  }, [regularOptions]);
  const filteredOptions = useMemo(() => {
    if (!showSearch) return options;
    const query = searchQuery.trim();
    if (query === '') return options;
    const results = fuse.current.search(query);
    const seen = new Set(results.map(r => r.item));
    for (const r_0 of fuseVowelStripped.current.search(stripVowels(query))) {
      if (!seen.has(r_0.item)) results.push(r_0);
    }
    return results.sort((a, b) => (a.score ?? 1) - (b.score ?? 1)).map(r_1 => r_1.item);
  }, [options, searchQuery, showSearch]);
  // Build display list with groups
  const displayList = useMemo(() => {
    if (groups.length === 0) {
      return filteredOptions;
    }
    const list: MultiSelectOption[] = [];
    const groupedOptions = new Map<string, MultiSelectOption[]>();

    // Group options
    filteredOptions.forEach(opt_1 => {
      if (opt_1.isGroup) return; // Skip group markers in options

      if (opt_1.groupId) {
        const existing = groupedOptions.get(opt_1.groupId);
        if (existing) {
          existing.push(opt_1);
        } else {
          groupedOptions.set(opt_1.groupId, [opt_1]);
        }
      } else {
        // Ungrouped options go at the end
        if (!groupedOptions.has('__ungrouped__')) {
          groupedOptions.set('__ungrouped__', []);
        }
        groupedOptions.get('__ungrouped__')!.push(opt_1);
      }
    });

    // Build list with group headers
    groups.forEach(group => {
      const groupOptions = groupedOptions.get(group.id);
      if (groupOptions && groupOptions.length > 0) {
        // Add group header (use group.id directly so it can be selected)
        list.push({
          id: group.id,
          label: group.label,
          isGroup: true
        });
        // Add group options
        list.push(...groupOptions);
      }
    });

    // Add ungrouped options
    const ungrouped = groupedOptions.get('__ungrouped__');
    if (ungrouped && ungrouped.length > 0) {
      list.push(...ungrouped);
    }
    return list;
  }, [filteredOptions, groups]);

  // Map group id -> child option ids for selectable groups
  const groupChildrenMap = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!selectableGroups || groups.length === 0) return map;
    for (const group_0 of groups) {
      map.set(group_0.id, options.filter(opt_2 => !opt_2.isGroup && opt_2.groupId === group_0.id).map(opt_3 => opt_3.id));
    }
    return map;
  }, [selectableGroups, groups, options]);
  const getGroupState = (groupId: string): 'all' | 'some' | 'none' => {
    const isGroupSelected = selectedIds.includes(groupId);
    if (!groupSelectionCascades) {
      return isGroupSelected ? 'all' : 'none';
    }
    const childIds = groupChildrenMap.get(groupId);
    if (!childIds || childIds.length === 0) return isGroupSelected ? 'all' : 'none';
    const selectedCount = childIds.filter(id => selectedIds.includes(id)).length;
    if (isGroupSelected && selectedCount === childIds.length) return 'all';
    if (isGroupSelected || selectedCount > 0) return 'some';
    return 'none';
  };
  const toggleGroup = (groupId_0: string) => {
    if (!groupSelectionCascades) {
      // Just toggle the group itself as a selectable option
      toggleOption(groupId_0);
      return;
    }
    const childIds_0 = groupChildrenMap.get(groupId_0) ?? [];
    const isGroupSelected_0 = selectedIds.includes(groupId_0);
    const allChildrenSelected = childIds_0.length > 0 && childIds_0.every(id_0 => selectedIds.includes(id_0));
    if (isGroupSelected_0 && allChildrenSelected) {
      // Deselect group + all children
      const toRemove = new Set([groupId_0, ...childIds_0]);
      onSelectionChange(selectedIds.filter(id_1 => !toRemove.has(id_1)));
    } else {
      // Select group + all children
      const newIds = new Set(selectedIds);
      newIds.add(groupId_0);
      childIds_0.forEach(id_2 => newIds.add(id_2));
      onSelectionChange(Array.from(newIds));
    }
  };

  // Setup virtualizer
  const virtualizer = useVirtualizer({
    count: displayList.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => estimateSize,
    overscan: 5,
    enabled: enableVirtualization
  });

  // Force virtualizer to measure when dropdown opens with data
  useEffect(() => {
    if (isOpen && enableVirtualization && displayList.length > 0) {
      virtualizer.measure();
    }
  }, [isOpen, enableVirtualization, displayList.length, virtualizer]);
  const toggleOption = (id_3: string) => {
    if (selectedIds.includes(id_3)) {
      onSelectionChange(selectedIds.filter(selectedId => selectedId !== id_3));
    } else {
      onSelectionChange([...selectedIds, id_3]);
    }
  };
  const removeOption = (id_4: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectionChange(selectedIds.filter(selectedId_0 => selectedId_0 !== id_4));
  };
  const selectAll = (e_0: React.MouseEvent) => {
    e_0.preventDefault();
    const selectableOptions = options.filter(opt_4 => !opt_4.isGroup);
    onSelectionChange(selectableOptions.map(opt_5 => opt_5.id));
  };
  const clearAll = (e_1: React.MouseEvent) => {
    e_1.preventDefault();
    onSelectionChange([]);
  };
  const selectedOptions = useMemo(() => {
    const opts = options.filter(opt_6 => selectedIds.includes(opt_6.id) && !opt_6.isGroup);
    if (selectableGroups) {
      const selectedGroupOpts = groups.filter(g => selectedIds.includes(g.id)).map(g_0 => ({
        id: g_0.id,
        label: g_0.label,
        isGroup: true
      }));
      return [...selectedGroupOpts, ...opts];
    }
    return opts;
  }, [options, groups, selectedIds, selectableGroups]);

  // Generate summary text for trigger
  const getSummaryText = () => {
    if (selectedIds.length === 0) return placeholder;
    if (showSummaryInTrigger) {
      const labels = selectedOptions.map(opt_7 => opt_7.label).join(', ');
      if (labels.length <= maxSummaryLength) {
        return labels;
      }

      // Truncate and add ellipsis
      return `${labels.substring(0, maxSummaryLength)}...`;
    }
    return `${selectedIds.length} selected`;
  };
  return <div className="space-y-2">
      {label && <label className="text-xs font-bold text-zinc-500 dark:text-zinc-500 uppercase tracking-widest block">
          {label}:
        </label>}

      <DropdownMenu modal={false} onOpenChange={open => {
      setIsOpen(open);
    }}>
        <DropdownMenuTrigger asChild>
          <button type="button" disabled={disabled} className={`w-full md:w-auto min-w-[240px] flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:border-zinc-300 dark:hover:border-zinc-700 transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} title={showSummaryInTrigger && selectedOptions.length > 0 ? selectedOptions.map(opt_8 => opt_8.label).join(', ') : undefined}>
            <span className="truncate">{getSummaryText()}</span>
            <ChevronDown className="w-4 h-4 text-zinc-400 transition-transform flex-shrink-0" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent updatePositionStrategy="optimized" side="top" sideOffset={4} className="w-[320px] max-h-[400px] bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-lg p-0" align="start" onCloseAutoFocus={e_2 => e_2.preventDefault()} onPointerDownOutside={e_3 => {
        // Allow clicking inside the dropdown content
        const target = e_3.target as Element;
        if (target.closest('[data-radix-dropdown-menu-content]')) {
          e_3.preventDefault();
        }
      }} onInteractOutside={e_4 => {
        // Prevent dialog from capturing events
        const target_0 = e_4.target as Element;
        if (target_0.closest('[data-radix-dropdown-menu-content]')) {
          e_4.preventDefault();
        }
      }}>
          {/* Search input - sticky at top */}
          {showSearch && <div className="sticky top-0 z-10 px-3 py-2 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input type="text" value={searchQuery} onChange={e_5 => setSearchQuery(e_5.target.value)} placeholder={searchPlaceholder} className="w-full pl-9 pr-3 py-2 text-sm bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400" onMouseDown={e_6 => e_6.stopPropagation()} autoComplete="off" />
              </div>
            </div>}

          {/* Bulk action buttons - sticky below search */}
          {showBulkActions && options.length > 0 && <div className={`sticky z-10 flex items-center justify-between gap-2 px-4 py-2 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 ${showSearch ? 'top-[52px]' : 'top-0'}`}>
              <button type="button" onMouseDown={selectAll} className="text-xs font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors cursor-pointer">
                Select All
              </button>
              <button type="button" onMouseDown={clearAll} className="text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors cursor-pointer">
                Clear Selection
              </button>
            </div>}

          <div ref={scrollContainerRef} className="max-h-[280px] overflow-y-auto p-2" onWheel={e_7 => {
          // Allow scrolling within this container
          e_7.stopPropagation();
        }} onTouchMove={e_8 => {
          // Allow touch scrolling
          e_8.stopPropagation();
        }}>
            {displayList.length === 0 ? <div className="px-3 py-8 text-center text-sm text-zinc-500 dark:text-zinc-400">
                {showSearch && searchQuery ? 'No results found' : 'No options available'}
              </div> : enableVirtualization ? <div style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative'
          }}>
                {virtualizer.getVirtualItems().map(virtualItem => {
              const option = displayList[virtualItem.index];
              const isSelected = selectedIds.includes(option.id);
              return <div key={virtualItem.key} data-index={virtualItem.index} ref={virtualizer.measureElement} style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`
              }}>
                      {option.isGroup ? <div onMouseDown={selectableGroups ? e_9 => {
                  e_9.preventDefault();
                  toggleGroup(option.id);
                } : undefined} className={`px-3 py-2.5 text-sm font-semibold text-zinc-700 dark:text-zinc-300 ${selectableGroups ? 'cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800 rounded-lg flex items-center gap-3' : ''}`}>
                          {selectableGroups && (() => {
                    const state = getGroupState(option.id);
                    return <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center transition-all flex-shrink-0 ${state === 'all' ? 'bg-teal-500 border-teal-500' : state === 'some' ? 'bg-teal-500/50 border-teal-500' : 'border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-950'}`}>
                                  {state === 'all' && <Check className="w-3 h-3 text-white" />}
                                  {state === 'some' && <Minus className="w-3 h-3 text-white" />}
                                </div>;
                  })()}
                          {option.label}
                        </div> : <div onMouseDown={e_10 => {
                  e_10.preventDefault();
                  toggleOption(option.id);
                }} className={`flex items-center gap-3 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors cursor-pointer rounded-lg ${option.groupId ? 'pl-6 pr-3' : 'px-3'}`}>
                          <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center transition-all flex-shrink-0 ${isSelected ? 'bg-teal-500 border-teal-500' : 'border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-950'}`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                            {option.label}
                          </span>
                        </div>}
                    </div>;
            })}
              </div> : displayList.map(option_0 => {
            const isSelected_0 = selectedIds.includes(option_0.id);
            if (option_0.isGroup) {
              return <div key={option_0.id} onMouseDown={selectableGroups ? e_11 => {
                e_11.preventDefault();
                toggleGroup(option_0.id);
              } : undefined} className={`px-3 py-2.5 text-sm font-semibold text-zinc-700 dark:text-zinc-300 ${selectableGroups ? 'cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800 rounded-lg flex items-center gap-3' : ''}`}>
                      {selectableGroups && (() => {
                  const state_0 = getGroupState(option_0.id);
                  return <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center transition-all flex-shrink-0 ${state_0 === 'all' ? 'bg-teal-500 border-teal-500' : state_0 === 'some' ? 'bg-teal-500/50 border-teal-500' : 'border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-950'}`}>
                              {state_0 === 'all' && <Check className="w-3 h-3 text-white" />}
                              {state_0 === 'some' && <Minus className="w-3 h-3 text-white" />}
                            </div>;
                })()}
                      {option_0.label}
                    </div>;
            }
            return <div key={option_0.id} onMouseDown={e_12 => {
              e_12.preventDefault();
              toggleOption(option_0.id);
            }} className={`flex items-center gap-3 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors cursor-pointer rounded-lg ${option_0.groupId ? 'pl-6 pr-3' : 'px-3'}`}>
                    <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center transition-all flex-shrink-0 ${isSelected_0 ? 'bg-teal-500 border-teal-500' : 'border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-950'}`}>
                      {isSelected_0 && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                      {option_0.label}
                    </span>
                  </div>;
          })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Selected items as chips */}
      {showChips && selectedOptions.length > 0 && <div className="flex flex-wrap gap-2">
          {selectedOptions.map(option_1 => <span key={option_1.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-50 dark:bg-teal-500/10 border border-teal-200 dark:border-teal-500/30 text-teal-700 dark:text-teal-400 text-xs font-bold">
              {option_1.label}
              <button type="button" onClick={e_13 => {
          removeOption(option_1.id, e_13);
        }} className="p-0.5 rounded-full hover:bg-teal-200 dark:hover:bg-teal-500/30 transition-colors">
                <X className="w-3 h-3" />
              </button>
            </span>)}
        </div>}
    </div>;
}